/**
 * Plan turns whose build already ran, per conversation. Kept in `localStorage` so every tab
 * makes the same "already built, don't start a build-stream" decision (it used to be
 * `sessionStorage`, which a second tab never saw). Plan: PROTOTYPER_STABILITY_TECH_PLAN.md
 * §6 (P2.4); P6.4 replaces this with server state.
 */
const STORAGE_PREFIX = 'ideoz:wbt:built-turns:v1:';
/** Oldest ids are dropped past this so one long conversation cannot fill the quota. */
const MAX_IDS_PER_CONVERSATION = 200;

function storageKey(conversationId: string): string {
  return `${STORAGE_PREFIX}${conversationId}`;
}

function parseIds(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === 'string' && id.length > 0);
  } catch {
    return [];
  }
}

function readStorage(storage: Storage | undefined, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function readBuiltTurnIds(conversationId: string | undefined): Set<string> {
  if (!conversationId) return new Set();
  const key = storageKey(conversationId);
  // Ids written by this tab before the move to localStorage still count.
  return new Set([
    ...parseIds(readStorage(globalThis.localStorage, key)),
    ...parseIds(readStorage(globalThis.sessionStorage, key)),
  ]);
}

export function writeBuiltTurnIds(
  conversationId: string | undefined,
  ids: ReadonlySet<string>
): void {
  if (!conversationId || typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(
      storageKey(conversationId),
      JSON.stringify([...ids].slice(-MAX_IDS_PER_CONVERSATION))
    );
  } catch {
    /* ignore quota */
  }
}

export function addBuiltTurnId(
  conversationId: string | undefined,
  llmInteractionId: string
): Set<string> {
  const next = readBuiltTurnIds(conversationId);
  next.add(llmInteractionId);
  writeBuiltTurnIds(conversationId, next);
  return next;
}
